import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaDownload, FaTrash, FaHistory, FaSpinner, FaCheckCircle, FaExclamationCircle, FaUpload, FaFileDownload, FaClock, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import './BackupManager.css';

const BackupManager = () => {
  const { t, language } = useLanguage();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);
  
  // Настройки автобекапа
  const [autoBackupSettings, setAutoBackupSettings] = useState({
    enabled: false,
    interval: { type: 'days', value: 1 },
    time: { hour: 3, minute: 0 }
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  // Локальное состояние для input значения интервала (чтобы можно было вводить пустое значение)
  const [intervalValueInput, setIntervalValueInput] = useState('1');
  // Локальные состояния для полей времени (чтобы можно было вводить пустое значение)
  const [timeHourInput, setTimeHourInput] = useState('3');
  const [timeMinuteInput, setTimeMinuteInput] = useState('00');

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/backup/list`);
      if (response.data.success) {
        setBackups(response.data.backups);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      showMessage('error', t('admin.backupLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBackups();
    fetchAutoBackupSettings();
  }, [fetchBackups]);

  // Загрузка настроек автобекапа
  const fetchAutoBackupSettings = async () => {
    try {
      setLoadingSettings(true);
      const url = `${API_BASE_URL}/api/backup/settings`;
      console.log('🔵 Fetching auto backup settings from:', url);
      console.log('   API_BASE_URL:', API_BASE_URL);
      const response = await axios.get(url);
      console.log('✅ Settings fetched successfully:', response.data);
      if (response.data.success) {
        setAutoBackupSettings(response.data.settings);
        setIntervalValueInput(String(response.data.settings.interval.value || 1));
        setTimeHourInput(String(response.data.settings.time?.hour ?? 3));
        // Добавляем ведущий ноль для минут
        const minute = response.data.settings.time?.minute ?? 0;
        setTimeMinuteInput(String(minute).padStart(2, '0'));
      }
    } catch (error) {
      console.error('❌ Error fetching auto backup settings:', error);
      console.error('   URL:', `${API_BASE_URL}/api/backup/settings`);
      console.error('   Response:', error.response?.data);
      console.error('   Status:', error.response?.status);
    } finally {
      setLoadingSettings(false);
    }
  };

  // Сохранение настроек автобекапа
  const saveAutoBackupSettings = async (newSettings) => {
    try {
      setSavingSettings(true);
      const url = `${API_BASE_URL}/api/backup/settings`;
      console.log('🔵 Saving auto backup settings to:', url);
      console.log('   Current settings:', autoBackupSettings);
      console.log('   New settings:', newSettings);
      console.log('   Interval changed:', {
        type: autoBackupSettings.interval.type !== newSettings.interval.type,
        value: autoBackupSettings.interval.value !== newSettings.interval.value
      });
      const response = await axios.post(url, newSettings);
      console.log('✅ Settings saved successfully:', response.data);
      console.log('   Received nextBackup:', response.data.settings?.nextBackup);
      if (response.data.success) {
        setAutoBackupSettings(response.data.settings);
        console.log('   Updated state with nextBackup:', response.data.settings.nextBackup);
        // Синхронизируем локальные состояния input с сохраненными значениями
        setIntervalValueInput(String(response.data.settings.interval.value || 1));
        setTimeHourInput(String(response.data.settings.time?.hour ?? 3));
        // Добавляем ведущий ноль для минут
        const minute = response.data.settings.time?.minute ?? 0;
        setTimeMinuteInput(String(minute).padStart(2, '0'));
        // Сообщение об успешном сохранении удалено по запросу
      }
    } catch (error) {
      console.error('❌ Error saving auto backup settings:', error);
      console.error('   URL:', `${API_BASE_URL}/api/backup/settings`);
      console.error('   Response:', error.response?.data);
      console.error('   Status:', error.response?.status);
      showMessage('error', t('admin.autoBackupSettingsError') || 'Error saving auto backup settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Обработчик изменения включения/выключения
  const handleToggleAutoBackup = async (enabled) => {
    const newSettings = { ...autoBackupSettings, enabled };
    await saveAutoBackupSettings(newSettings);
  };

  // Обработчик изменения типа интервала (days/hours/minutes)
  const handleIntervalTypeChange = async (type) => {
    const newSettings = {
      ...autoBackupSettings,
      interval: { type, value: autoBackupSettings.interval.value || 1 }
    };
    await saveAutoBackupSettings(newSettings);
  };

  // Обработчик изменения значения интервала (только для input)
  const handleIntervalValueInputChange = (value) => {
    // Разрешаем пустое значение и любые цифры
    if (value === '' || /^\d+$/.test(value)) {
      setIntervalValueInput(value);
    }
  };

  // Сохранение значения интервала при blur или Enter
  const handleIntervalValueBlur = async () => {
    const numValue = parseInt(intervalValueInput);
    const finalValue = (numValue && numValue > 0) ? numValue : 1;
    
    // Обновляем локальное состояние, если значение было пустым или невалидным
    if (!intervalValueInput || numValue <= 0) {
      setIntervalValueInput(String(finalValue));
    }
    
    // Сохраняем только если значение изменилось
    if (finalValue !== autoBackupSettings.interval.value) {
      const newSettings = {
        ...autoBackupSettings,
        interval: { type: autoBackupSettings.interval.type, value: finalValue }
      };
      await saveAutoBackupSettings(newSettings);
    }
  };

  // Обработчик нажатия Enter в input
  const handleIntervalValueKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Обработчик изменения часа (только для input)
  const handleTimeHourInputChange = (value) => {
    // Разрешаем пустое значение и любые цифры от 0 до 23
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 23)) {
      setTimeHourInput(value);
    }
  };

  // Обработчик изменения минут (только для input)
  const handleTimeMinuteInputChange = (value) => {
    // Разрешаем пустое значение и любые цифры от 0 до 59
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 59)) {
      setTimeMinuteInput(value);
    }
  };

  // Сохранение времени при blur или Enter
  const handleTimeBlur = async () => {
    const hourValue = parseInt(timeHourInput);
    const minuteValue = parseInt(timeMinuteInput);
    
    // Валидация и нормализация значений
    const finalHour = (hourValue >= 0 && hourValue <= 23) ? hourValue : 3;
    const finalMinute = (minuteValue >= 0 && minuteValue <= 59) ? minuteValue : 0;
    
    // Обновляем локальные состояния, если значения были невалидными
    if (!timeHourInput || hourValue < 0 || hourValue > 23) {
      setTimeHourInput(String(finalHour));
    }
    if (!timeMinuteInput || minuteValue < 0 || minuteValue > 59) {
      // Добавляем ведущий ноль для минут
      setTimeMinuteInput(String(finalMinute).padStart(2, '0'));
    }
    
    // Сохраняем только если значения изменились
    if (finalHour !== autoBackupSettings.time.hour || finalMinute !== autoBackupSettings.time.minute) {
      const newSettings = {
        ...autoBackupSettings,
        time: { hour: finalHour, minute: finalMinute }
      };
      await saveAutoBackupSettings(newSettings);
    }
  };

  // Обработчик нажатия Enter в полях времени
  const handleTimeKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm(t('admin.createBackupConfirm'))) {
      return;
    }

    try {
      setCreating(true);
      setMessage({ type: '', text: '' });
      const response = await axios.post(`${API_BASE_URL}/api/backup/create`);
      
      if (response.data.success) {
        showMessage('success', t('admin.backupCreated'));
        fetchBackups();
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      showMessage('error', t('admin.backupCreateError') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setCreating(false);
    }
  };

  const handleRestoreBackup = async (backupId) => {
    if (!window.confirm(t('admin.restoreBackupConfirm'))) {
      return;
    }

    try {
      setRestoring(backupId);
      setMessage({ type: '', text: '' });
      const response = await axios.post(`${API_BASE_URL}/api/backup/restore/${backupId}`);
      
      if (response.data.success) {
        showMessage('success', t('admin.backupRestored'));
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      showMessage('error', t('admin.backupRestoreError') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setRestoring(null);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm(t('admin.deleteBackupConfirm'))) {
      return;
    }

    try {
      setDeleting(backupId);
      setMessage({ type: '', text: '' });
      const response = await axios.delete(`${API_BASE_URL}/api/backup/${backupId}`);
      
      if (response.data.success) {
        showMessage('success', t('admin.backupDeleted'));
        fetchBackups();
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      showMessage('error', t('admin.backupDeleteError') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      setMessage({ type: '', text: '' });
      
      // Используем query параметр для надежной передачи ID
      const encodedId = encodeURIComponent(backupId);
      const downloadUrl = `${API_BASE_URL}/api/backup/download?id=${encodedId}`;
      
      console.log(`📥 Downloading backup: ${backupId}`);
      console.log(`   URL: ${downloadUrl}`);
      console.log(`   API Base URL: ${API_BASE_URL}`);
      
      let response;
      try {
        response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/gzip, application/x-gzip, application/json, */*'
          },
        });
      } catch (fetchError) {
        console.error('❌ Network error during fetch:', fetchError);
        throw new Error(`Network error: ${fetchError.message}. Please check your connection and try again.`);
      }

      // Проверяем тип контента для определения ошибки
      const contentType = response.headers.get('content-type') || '';
      const contentLength = response.headers.get('content-length');
      
      console.log(`   Response status: ${response.status}`);
      console.log(`   Content-Type: ${contentType}`);
      console.log(`   Content-Length: ${contentLength}`);
      
      if (!response.ok) {
        let errorMessage = `Server error: ${response.status} ${response.statusText}`;
        
        // Если это JSON ошибка, парсим её
        if (contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
            console.error('❌ Server returned JSON error:', errorData);
          } catch (parseError) {
            console.error('❌ Error parsing JSON error response:', parseError);
          }
        } else if (contentType.includes('text/html') || contentType.includes('text/plain')) {
          // Если это HTML ошибка (404 страница), читаем текст
          try {
            const errorText = await response.text();
            console.error('❌ Server error response (HTML/text):', errorText.substring(0, 500));
            
            // Пытаемся извлечь полезную информацию из HTML
            if (errorText.includes('Cannot GET') || errorText.includes('404')) {
              errorMessage = `Backup endpoint not found (404). Please check server configuration.`;
            } else if (errorText.includes('500')) {
              errorMessage = `Server error (500). Please try again later.`;
            }
          } catch (textError) {
            console.error('❌ Error reading error text:', textError);
          }
        }
        
        // Специальная обработка для разных статусов
        if (response.status === 404) {
          errorMessage = `Backup not found (404). The backup may have been deleted.`;
        } else if (response.status === 400) {
          errorMessage = `Invalid request (400). ${errorMessage}`;
        } else if (response.status === 500) {
          errorMessage = `Server error (500). ${errorMessage}`;
        }
        
        throw new Error(errorMessage);
      }

      // Проверяем, что это действительно файл, а не ошибка
      const isGzipFile = contentType.includes('application/gzip') || 
                         contentType.includes('application/x-gzip') ||
                         contentType.includes('application/octet-stream');
      
      if (!isGzipFile && contentType.includes('application/json')) {
        // Если сервер вернул JSON вместо файла, это ошибка
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || errorData.message || 'Server returned error instead of file');
        } catch (parseError) {
          throw new Error('Server returned unexpected content type (JSON instead of file)');
        }
      }
      
      if (!isGzipFile && contentType) {
        const text = await response.text();
        console.error('❌ Unexpected content type:', contentType);
        console.error('   Response preview:', text.substring(0, 500));
        throw new Error(`Server returned unexpected content type: ${contentType}`);
      }

      // Получаем имя файла из заголовка или используем backupId
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${backupId}.tar.gz`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Декодируем имя файла если оно было закодировано
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // Если декодирование не удалось, используем как есть
          }
        }
      }

      console.log(`   Downloading file: ${filename}`);

      // Создаем blob и скачиваем
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty (0 bytes). The backup may be corrupted.');
      }
      
      console.log(`   File size: ${blob.size} bytes`);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Небольшая задержка перед очисткой
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      console.log(`✅ Backup downloaded successfully: ${filename}`);
      showMessage('success', t('admin.backupDownloaded'));
    } catch (error) {
      console.error('❌ Error downloading backup:', error);
      console.error('   Error details:', {
        message: error.message,
        stack: error.stack,
        backupId: backupId
      });
      
      // Показываем понятное сообщение об ошибке
      let errorMessage = error.message;
      if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Connection error. Please check your internet connection and server status.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Backup not found. It may have been deleted.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Server error. Please try again later or contact support.';
      }
      
      showMessage('error', t('admin.backupDownloadError') + ': ' + errorMessage);
    }
  };

  const handleUploadBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!file.name.endsWith('.tar.gz')) {
      showMessage('error', t('admin.backupUploadInvalidFormat'));
      return;
    }

    try {
      setUploading(true);
      setMessage({ type: '', text: '' });

      const formData = new FormData();
      formData.append('backup', file);

      const response = await axios.post(`${API_BASE_URL}/api/backup/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        showMessage('success', t('admin.backupUploaded'));
        fetchBackups();
      }
    } catch (error) {
      console.error('Error uploading backup:', error);
      showMessage('error', t('admin.backupUploadError') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
      // Сбрасываем input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => {
      setMessage({ type: '', text: '' });
    }, 5000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = date - now;
    
    // Если время уже прошло (в пределах 1 минуты), показываем "сейчас"
    if (diff <= 0 && diff > -60000) { // в пределах 1 минуты назад
      return language === 'en' ? 'now' : 'сейчас';
    }
    
    // Если время в будущем, но меньше 1 часа, показываем точное количество минут
    if (diff > 0 && diff < 3600000) {
      // Используем Math.floor для точного отображения минут (не округляем вверх)
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      // Если меньше минуты, показываем секунды
      if (minutes === 0) {
        return language === 'en' ? `in ${seconds} second(s)` : `через ${seconds} секунд(ы)`;
      }
      
      // Для интервалов меньше 2 минут показываем секунды для точности
      // Для больших интервалов - только минуты (округляем)
      if (minutes < 2 && seconds > 0) {
        return language === 'en' 
          ? `in ${minutes} minute(s) ${seconds} second(s)`
          : `через ${minutes} минут(ы) ${seconds} секунд(ы)`;
      }
      
      // Только минуты (округляем вверх если есть секунды)
      const roundedMinutes = seconds > 0 ? minutes + 1 : minutes;
      return language === 'en' ? `in ${roundedMinutes} minute(s)` : `через ${roundedMinutes} минут(ы)`;
    }
    
    // Если время в будущем, но меньше 24 часов, показываем через сколько часов и минут
    if (diff > 0 && diff < 86400000) {
      const totalMinutes = Math.floor(diff / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      if (hours === 0) {
        return language === 'en' ? `in ${minutes} minute(s)` : `через ${minutes} минут(ы)`;
      }
      
      if (minutes === 0) {
      return language === 'en' ? `in ${hours} hour(s)` : `через ${hours} часов`;
    }
    
      return language === 'en' 
        ? `in ${hours} hour(s) ${minutes} minute(s)`
        : `через ${hours} часов ${minutes} минут(ы)`;
    }
    
    // Форматируем дату в формате DD/MM/YYYY, HH:MM
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    // Определяем AM/PM для английского языка
    if (language === 'en') {
      const hour12 = date.getHours() % 12 || 12;
      const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
      return `${day}/${month}/${year}, ${hour12}:${minutes} ${ampm}`;
    } else {
      return `${day}/${month}/${year}, ${hours}:${minutes}`;
    }
  };

  return (
    <div className="backup-manager">
      <div className="backup-header">
        <h2>
          <FaHistory /> {t('admin.backupTitle')}
        </h2>
        <div className="backup-header-actions">
          <label className="upload-backup-btn" title={t('admin.uploadBackupTitle')}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz"
              onChange={handleUploadBackup}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            {uploading ? (
              <>
                <FaSpinner className="spinning" /> {t('admin.uploading')}
              </>
            ) : (
              <>
                <FaUpload /> {t('admin.uploadBackup')}
              </>
            )}
          </label>
          <button
            className="create-backup-btn"
            onClick={handleCreateBackup}
            disabled={creating}
          >
            {creating ? (
              <>
                <FaSpinner className="spinning" /> {t('admin.creating')}
              </>
            ) : (
              <>
                <FaDownload /> {t('admin.createBackup')}
              </>
            )}
          </button>
        </div>
      </div>

      {message.text && (
        <motion.div
          className={`backup-message ${message.type}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
          <span>{message.text}</span>
        </motion.div>
      )}

      {loading ? (
        <div className="backup-loading">
          <FaSpinner className="spinning" /> {t('admin.loadingBackups')}
        </div>
      ) : backups.length === 0 ? (
        <div className="backup-empty">
          <p>{t('admin.noBackups')}</p>
          <p className="backup-hint">{t('admin.noBackupsHint')}</p>
        </div>
      ) : (
        <div className="backups-list">
          {backups.map((backup) => (
            <motion.div
              key={backup.id}
              className="backup-item"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="backup-info">
                <div className="backup-name">
                  <strong>{backup.name}</strong>
                  <span className="backup-type">{backup.type === 'archive' ? t('admin.backupTypeArchive') : t('admin.backupTypeFolder')}</span>
                </div>
                <div className="backup-details">
                  <span className="backup-date">
                    {t('admin.backupCreatedAt')}: {formatDate(backup.createdAt)}
                  </span>
                  <span className="backup-size">
                    {t('admin.backupSize')}: {backup.formattedSize}
                  </span>
                </div>
              </div>
              <div className="backup-actions">
                <button
                  className="download-btn"
                  onClick={() => handleDownloadBackup(backup.id)}
                  disabled={restoring === backup.id || deleting === backup.id}
                  title={t('admin.downloadBackupTitle')}
                >
                  <FaFileDownload /> {t('admin.downloadBackup')}
                </button>
                <button
                  className="restore-btn"
                  onClick={() => handleRestoreBackup(backup.id)}
                  disabled={restoring === backup.id || deleting === backup.id}
                  title={t('admin.restoreBackupTitle')}
                >
                  {restoring === backup.id ? (
                    <>
                      <FaSpinner className="spinning" /> {t('admin.restoring')}
                    </>
                  ) : (
                    <>
                      <FaDownload /> {t('admin.restoreBackup')}
                    </>
                  )}
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteBackup(backup.id)}
                  disabled={restoring === backup.id || deleting === backup.id}
                  title={t('admin.deleteBackupTitle')}
                >
                  {deleting === backup.id ? (
                    <>
                      <FaSpinner className="spinning" /> {t('admin.deleting')}
                    </>
                  ) : (
                    <>
                      <FaTrash /> {t('admin.deleteBackup')}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Настройки автобекапа */}
      <div className="auto-backup-settings">
        <h3>
          <FaClock /> {t('admin.autoBackupSettings') || 'Automatic Backup Settings'}
        </h3>
        
        {loadingSettings ? (
          <div className="backup-loading">
            <FaSpinner className="spinning" /> {t('admin.loading') || 'Loading...'}
          </div>
        ) : (
          <div className="auto-backup-controls">
            {/* Переключатель включения/выключения */}
            <div className="auto-backup-toggle">
              <label className="toggle-label">
                <span>{t('admin.enableAutoBackup') || 'Enable Automatic Backups'}</span>
                <div className="toggle-button-wrapper">
                  <button
                    type="button"
                    className={`toggle-button ${autoBackupSettings.enabled ? 'enabled' : 'disabled'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Toggle clicked, current enabled:', autoBackupSettings.enabled);
                      handleToggleAutoBackup(!autoBackupSettings.enabled);
                    }}
                    disabled={savingSettings}
                    aria-label={autoBackupSettings.enabled ? 'Disable auto backup' : 'Enable auto backup'}
                    title={autoBackupSettings.enabled ? (t('admin.autoBackupEnabled') || 'Automatic backup is enabled') : (t('admin.autoBackupDisabled') || 'Automatic backup is disabled')}
                  >
                    {/* Иконки скрыты, используется CSS toggle */}
                    {autoBackupSettings.enabled ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                    <span className="auto-backup-status-hint">
                    {autoBackupSettings.enabled 
                      ? (t('admin.autoBackupEnabled') || 'Automatic backup is enabled')
                      : (t('admin.autoBackupDisabled') || 'Automatic backup is disabled')
                    }
                    </span>
                </div>
              </label>
            </div>

            {autoBackupSettings.enabled && (
              <motion.div
                className="auto-backup-options"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                {/* Настройка интервала */}
                <div className="backup-interval-setting">
                  <label>{t('admin.backupInterval') || 'Backup Interval'}</label>
                  <div className="interval-controls">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="1"
                      value={intervalValueInput}
                      onChange={(e) => handleIntervalValueInputChange(e.target.value)}
                      onBlur={handleIntervalValueBlur}
                      onKeyPress={handleIntervalValueKeyPress}
                      disabled={savingSettings}
                      className="interval-value-input"
                      placeholder="1"
                    />
                    <select
                      value={autoBackupSettings.interval.type}
                      onChange={(e) => handleIntervalTypeChange(e.target.value)}
                      disabled={savingSettings}
                      className="interval-type-select"
                    >
                      <option value="days">{t('admin.days') || 'Days'}</option>
                      <option value="hours">{t('admin.hours') || 'Hours'}</option>
                      <option value="minutes">{t('admin.minutes') || 'Minutes'}</option>
                    </select>
                  </div>
                </div>

                {/* Настройка времени (только для интервалов в днях) */}
                {autoBackupSettings.interval.type === 'days' && (
                  <div className="backup-time-setting">
                    <label>{t('admin.backupTime') || 'Backup Time'}</label>
                    <div className="time-controls">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      max="23"
                      value={timeHourInput}
                      onChange={(e) => handleTimeHourInputChange(e.target.value)}
                      onBlur={handleTimeBlur}
                      onKeyPress={handleTimeKeyPress}
                      disabled={savingSettings}
                      className="time-input"
                      placeholder="3"
                    />
                    <span>:</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      max="59"
                      value={timeMinuteInput}
                      onChange={(e) => handleTimeMinuteInputChange(e.target.value)}
                      onBlur={handleTimeBlur}
                      onKeyPress={handleTimeKeyPress}
                      disabled={savingSettings}
                      className="time-input"
                      placeholder="0"
                    />
                    </div>
                  </div>
                )}

                {/* Информация о следующем бэкапе */}
                {autoBackupSettings.nextBackup && (
                  <div className="next-backup-info">
                    <span>{t('admin.nextBackup') || 'Next backup'}: {formatDate(autoBackupSettings.nextBackup)}</span>
                  </div>
                )}

                {savingSettings && (
                  <div className="saving-indicator">
                    <FaSpinner className="spinning" /> {t('admin.saving') || 'Saving...'}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        )}
      </div>

      <div className="backup-info-section">
        <h3>{t('admin.backupInfo')}</h3>
        <ul>
          <li>{t('admin.backupInfo1')}</li>
          <li>{t('admin.backupInfo2')}</li>
          <li>{t('admin.backupInfo3')}</li>
          <li>{t('admin.backupInfo4')}</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManager;

